import {
  Suspense,
  useEffect,
  useState,
  type ReactElement
} from "react";
import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import marketingCss from "../../styles/marketing.css?inline";
import { MarketingTopNav } from "./MarketingTopNav";
import { isAccountAppRoute } from "./accountAppRoutes";
import {
  ViewerTopNav,
  VIEWER_TOP_NAV_EXPORT_HEADER_ID,
  viewerTopNavPlaceholderModel,
  type ViewerTopNavModel
} from "./ViewerTopNav";
import { ViewerModuleStrip } from "../viewer/ViewerModuleStrip";
import {
  registerViewerTopNavBridge,
  type ViewerIllustrationCaptureNav
} from "./viewerTopNavBridge";
import { ViewerShellModelProvider } from "../viewer/viewerShellModelContext";
import { DesignTokensStyle } from "../../styles/DesignTokensStyle";
import { ThemePreferenceProvider } from "../../styles/ThemePreferenceProvider";
import "./appTopNavShell.css";
import "../../styles/instantHover.css";

/**
 * Persistent shell that wraps every route.
 *
 * The single `<header id="app-top-nav">` element is the centerpiece of the
 * SPA migration's "no flicker" promise: because it's the SAME React node
 * across `/` and `/viewer`, its DOM element is preserved across route
 * transitions. Only the inner content swaps — `<MarketingTopNav />` on the
 * marketing route, `<ViewerTopNav />` on the viewer route — and that swap
 * is a normal React child reconciliation, not a remount.
 *
 * The dev-only `/map` dashboard is an exception: the entire header is
 * omitted there so the map is full-viewport without marketing chrome.
 *
 * The viewer top nav's data dependencies (live address, DEM tier label,
 * report token expiry, export handler) come from the imperative
 * `src/main.ts` orchestration via the `viewerTopNavBridge` module. RootLayout
 * registers React state setters with the bridge on mount; main.ts calls
 * `publishViewerTopNavModel(…)` whenever the model changes. Until the first
 * push (chunk load + `bootSite`), RootLayout derives a synchronous placeholder
 * from the URL (`viewerTopNavPlaceholderModel`) so `/viewer` never renders an
 * empty header. That replaces the old "mount a React root into
 * #viewer-top-nav-mount and bump it" pattern.
 *
 * The merged `/viewer` headline model is also passed through
 * `ViewerShellModelProvider` so footer HUD React islands (map meta chip)
 * stay in sync without touching the DOM from `main.ts`.
 *
 * Suspense wraps the Outlet so the React.lazy chunks (MarketingPage,
 * ViewerPage) can defer rendering without dropping the header. Fallback is
 * `null` to avoid any visual flash; the page bodies cross-fade naturally
 * once their CSS is injected.
 */
export function RootLayout(): ReactElement {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const isViewer = pathname.startsWith("/viewer");
  /**
   * Dev-only full-bleed dashboards: `/map` (Mapbox), `/emails` (template
   * preview), `/modals` (sidebar + iframe modal preview), and the
   * `/modals-preview` host that `/modals` loads inside its iframe. None
   * of them want the marketing chrome on top.
   */
  const hideTopNav =
    pathname === "/map" ||
    pathname === "/emails" ||
    pathname === "/modals" ||
    pathname === "/modals-preview";
  const isAccountApp = isAccountAppRoute(pathname);

  const [viewerModel, setViewerModel] = useState<ViewerTopNavModel | null>(
    null
  );
  const [viewerExportHandler, setViewerExportHandler] = useState<
    (() => void) | null
  >(null);
  const [viewerPdfHandler, setViewerPdfHandler] = useState<
    (() => void) | null
  >(null);
  const [mobileExportMenuOpen, setMobileExportMenuOpen] = useState(false);
  const [illustrationCaptureNav, setIllustrationCaptureNav] =
    useState<ViewerIllustrationCaptureNav | null>(null);

  const viewerMergedModel =
    isViewer ? (viewerModel ?? viewerTopNavPlaceholderModel(searchParams)) : null;

  useEffect(() => {
    if (!isViewer) {
      setMobileExportMenuOpen(false);
      setIllustrationCaptureNav(null);
    }
  }, [isViewer]);

  useEffect(() => {
    // Bridge is registered once for the lifetime of the root layout. The
    // setters close over React's state, so main.ts can push fresh models
    // without knowing anything about React.
    const teardown = registerViewerTopNavBridge({
      setModel: setViewerModel,
      setExportHandler: (handler) => {
        // useState with a function value: pass a thunk that returns the
        // function, otherwise React would invoke it during the update.
        setViewerExportHandler(() => handler);
      },
      setPdfHandler: (handler) => {
        setViewerPdfHandler(() => handler);
      },
      setIllustrationCapture: setIllustrationCaptureNav
    });
    return teardown;
  }, []);

  return (
    <ThemePreferenceProvider>
      <DesignTokensStyle />
      {isAccountApp ? <style>{marketingCss}</style> : null}
      {!hideTopNav ? (
        <header id="app-top-nav">
          {isViewer ? (
            <ViewerTopNav
              model={viewerMergedModel!}
              exportButtonId={VIEWER_TOP_NAV_EXPORT_HEADER_ID}
              onExportScreenshot={() => viewerExportHandler?.()}
              onExportPdf={
                viewerPdfHandler ? () => viewerPdfHandler() : undefined
              }
              illustrationCaptureActive={illustrationCaptureNav != null}
              onIllustrationCancel={illustrationCaptureNav?.onCancel}
              onIllustrationContinue={illustrationCaptureNav?.onContinue}
              mobileExportMenuOpen={mobileExportMenuOpen}
              onMobileExportMenuOpenChange={setMobileExportMenuOpen}
            />
          ) : (
            <MarketingTopNav />
          )}
        </header>
      ) : null}
      <ViewerShellModelProvider value={viewerMergedModel}>
        <Suspense fallback={null}>
          <Outlet />
        </Suspense>
      </ViewerShellModelProvider>
      {isViewer && viewerMergedModel ? <ViewerModuleStrip /> : null}
    </ThemePreferenceProvider>
  );
}
